import { LessonInfo, PrepLessons, Text, Button, SimpleText } from 'platform-components';
import { POPUP_DIRECTIONS } from 'platform-components/constants';
import React, { useState } from 'react';
import ym from 'react-yandex-metrika';

import usePrepLessonClick, { MetrikaParams } from 'common.components/hooks/use-prep-lesson-click';
import Usage from 'common.components/usage/usage';
import { getIsRepeatable } from 'utils/lesson';
import { getIsDeadlinePassed } from 'utils/lesson/dates/dates';

import './promo__prep-lessons.scss';

interface Props {
    isLoading: boolean;
    prepLessons: Array<LessonInfo>;
    now: Date | null;
    metrikaParams: MetrikaParams;
}

const MAX_COLLAPSED_COUNT = 6;

const PromoPrepLessons = (props: Props) => {
    const { isLoading, prepLessons, now, metrikaParams } = props;

    const [isAllShown, showAll] = useState(prepLessons.length <= MAX_COLLAPSED_COUNT);
    const { handlePrepLessonClick } = usePrepLessonClick(metrikaParams);

    if (!isLoading && (!prepLessons || !prepLessons.length)) {
        return null;
    }

    const renderAssignedWarning = (isRepeatable: boolean, assignedCount?: number): React.ReactNode | null => {
        const shouldShowAssignedWarning = Boolean(assignedCount && assignedCount > 0 && isRepeatable);

        if (!shouldShowAssignedWarning) {
            return null;
        }

        return (
            <Usage
                mix="theme-page__prep-lesson-item-usage"
                popupDirection={POPUP_DIRECTIONS.TOP}
                tooltipMix="theme-page__prep-lesson-item-usage-tooltip"
            >
                <Text id="library.prepLessons.assignedWarning" type={Text.TYPE.MARKDOWN} />
            </Usage>
        );
    };

    const handleShowAllClick = () => {
        ym('reachGoal', 'teacher_vitrina_subtopic_view_all', metrikaParams);
        showAll(true);
    };

    return (
        <div className="promo__prep-lessons-wrapper">
            <PrepLessons
                isLoading={isLoading}
                mix="promo__prep-lessons"
                shouldShowSkeletonLoader={true}
                theme={PrepLessons.THEMES.GRID}
            >
                {/* eslint-disable-next-line complexity */}
                {prepLessons &&
                    prepLessons.map((lesson, index) => {
                        const { id, meta, assignedCount } = lesson;
                        const { accessible_to_teacher: accessibleToTeacher, date_deadline: dateDeadline } = meta;
                        const isRepeatable = getIsRepeatable(meta);

                        const isDateDeadlinedPassed = getIsDeadlinePassed(
                            dateDeadline ? new Date(dateDeadline) : null,
                            now
                        );
                        const canView = !isDateDeadlinedPassed;

                        if (!isAllShown && index >= MAX_COLLAPSED_COUNT) {
                            return null;
                        }

                        const lessonMetrikaParams = {
                            section_element_number: index + 1,
                        };

                        return (
                            // @ts-ignore
                            <PrepLessons.Item
                                assignedWarning={renderAssignedWarning(isRepeatable, assignedCount)}
                                // @ts-ignore
                                buttonTextId="library.prepLessons.lookAndAssign"
                                disabled={!canView}
                                endDate={dateDeadline ? new Date(dateDeadline) : null}
                                index={index}
                                key={id}
                                lesson={lesson}
                                onClick={() => {
                                    handlePrepLessonClick(id, lessonMetrikaParams);
                                }}
                                shouldShowButton={true}
                                startDate={accessibleToTeacher ? new Date(accessibleToTeacher) : null}
                            />
                        );
                    })}
            </PrepLessons>
            {!isAllShown && (
                <Button className="promo__show-all-btn" onClick={handleShowAllClick} view={Button.VIEW.SECONDARY}>
                    <SimpleText id="promo.prepLessons.showAll" />
                </Button>
            )}
        </div>
    );
};

export default PromoPrepLessons;
